// QuestionRenderer.jsx
import React from "react";
import ViewEditor from "../../components/common/editor/ViewEditor";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertFromRaw } from "draft-js";
import CheckBox from "../../components/common/form/CheckBox";
import CheckBoxInline from "../../components/common/form/CheckBoxInline";
import Radio from "../../components/common/form/Radio";
import RadioInline from "../../components/common/form/RadioInline";
import SubmissionFileUpload from "../../components/common/form/SubmissionFileUpload";
import QuestionButton from "../../components/common/form/QuestionButton";
import { questionMCQAnswerTypeConstants, questionTypeConstants } from "../../helper/constants";

const QuestionRenderer = ({
  question,
  index,
  qIndex,
  answer,
  answers,
  tempQuestionState,
  tempAnswerStates,
  makeRevise,
  makeEdit,
  markAnswer,
  markOneAnswer,
  getSubmissionContent,
  deleteSubmissionContent,
  getQuestionPassageContent,
  filesUploaded,
  subQuestions,
  sections = [],
  renderSubQuestionEditor
}) => {
  const renderPassage = () => {
    if (question.is_passage && question.passage_id && question.passage_content) {
      return (
        <div className="mb-5 mt-5">
          <ViewEditor editorState={getQuestionPassageContent(question.passage_content)} />
        </div>
      );
    }
    return null;
  };

  const renderReviseBox = () => (
    <div className="pl-2 pr-2 border border-primary rounded d-inline-block push-button">
      <CheckBoxInline>
        <CheckBox
          key={qIndex}
          onClick={(e) => makeRevise(e, qIndex)}
          checked={question.needToRevise || false}
          checkBoxName="Mark for Review"
          style={{ color: "#5458AF" }}
        />
      </CheckBoxInline>
    </div>
  );

  const renderDescriptive = () => (
    <div className="col-md-12" style={{ overflow: "auto" }}>
      {renderPassage()}
      <h3 className="d-flex mb-5 overflow-hidden all-questions-title">
        <div className="number-wrap" style={{ fontSize: "12px", marginTop: "3px" }}>{index + 1}.</div>
        {Array.isArray(sections) && sections.length > 0 && (
          <div>
            {question.is_optional && (
              <span className="badge badge-primary mr-2">Optional</span>
            )}
          </div>
        )}
        <div className="rdw-editor-question">
          <ViewEditor editorState={tempQuestionState} />
        </div>
        {renderReviseBox()}
      </h3>
      <div className="RichEditor-root-descriptive-answer" key={qIndex} id="editAnswer">
        <Editor
          placeholder="Add your answer here..."
          editorStyle={{ height: "200px" }}
          editorState={answer}
          onEditorStateChange={(e) => makeEdit(e, qIndex, question.question_id)}
          toolbar={{
            fontFamily: {
              options: ["Arial", "Georgia", "Impact", "Tahoma", "Times New Roman", "Verdana", "Kushpoo", "Baamini", "FMAbhaya"]
            }
          }}
          toolbarClassName="vertical-toolbar"
        />
      </div>
      <div className="separator separator-dashed separator-border-2 mt-5 mb-5" />
    </div>
  );

  const renderSubmission = () => {
    let submissionList = answers[question.question_id] || [];
    let reload = submissionList.length > 0;

    return (
      <div className="col-md-12" style={{ overflow: "auto" }}>
        {renderPassage()}
        <h3 className="mb-5 overflow-hidden all-questions-title">
          <div className="d-flex">
            <div className="number-wrap" style={{ fontSize: "12px", marginTop: "3px" }}>{index + 1}.</div>
            {Array.isArray(sections) && sections.length > 0 && (
              <div>
                {question.is_optional && (
                  <span className="badge badge-primary mr-2">Optional</span>
                )}
              </div>
            )}
            <ViewEditor editorState={tempQuestionState} />
          </div>
          {renderReviseBox()}
        </h3>
        <SubmissionFileUpload
          className="col-md-10 col-form-label"
          key={qIndex}
          id={question.question_id}
          title={submissionList}
          defaultFile={submissionList}
          callBack={getSubmissionContent}
          deleteback={deleteSubmissionContent}
          reload={reload}
          questionIndex={question.question_id}
        />
        <div className="separator separator-dashed separator-border-2 mt-5 mb-5" />
      </div>
    );
  };

  const renderMCQ = () => (
    <div className="col-md-12 px-0" style={{ overflow: "auto" }}>
      {renderPassage()}
      <h3 className="mb-5 overflow-hidden all-questions-title">
        <div className="d-flex">
          <div className="number-wrap" style={{ fontSize: "12px", marginTop: "3px" }}>{index + 1}.</div>
          {Array.isArray(sections) && sections.length > 0 && (
            <div>
              {question.is_optional && (
                <span className="badge badge-primary mr-2">Optional</span>
              )}
            </div>
          )}
          <div className="rdw-editor-question">
            <ViewEditor editorState={tempQuestionState} />
          </div>
        </div>
        {renderReviseBox()}
      </h3>
      <div className={`RichEditor-root px-0 parent-class ${tempAnswerStates.some(answer => answer.getCurrentContent().getPlainText().length > 100) ? "single-column" : ""}`}>
        {tempAnswerStates.map((answerState, j) => (
          <div className="row d-flex align-items-center child-class" key={j}>
            <div className="col-md-2 col-2">
              {question.answer_type_id === questionMCQAnswerTypeConstants.One.id ? (
                <RadioInline>
                  <Radio
                    key={j}
                    onChange={(e) => markOneAnswer(e, question.question_id, j)}
                    checked={answers[question.question_id]?.[0] === j}
                    style={{ color: "#5458AF" }}
                    value={j}
                  />
                </RadioInline>
              ) : (
                <CheckBoxInline>
                  <CheckBox
                    key={j}
                    onClick={(e) => markAnswer(e, question.question_id, j)}
                    checked={answers[question.question_id]?.includes(j)}
                    style={{ color: "#5458AF" }}
                  />
                </CheckBoxInline>
              )}
            </div>
            <div className="col-md-10 col-10">
              <Editor
                editorState={answerState}
                toolbarClassName="hide-toolbar"
                readOnly={true}
                toolbar={{}}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="separator separator-dashed separator-border-2 mt-5 mb-5" />
    </div>
  );

  if (question.question_type === questionTypeConstants.DESCRIPTIVE.name) return renderDescriptive();
  if (question.question_type === questionTypeConstants.MCQ.name) return renderMCQ();
  if (question.question_type === questionTypeConstants.SUBMISSION.name) return renderSubmission();
  if (question.question_type === questionTypeConstants.SUBQUESTION.name) return renderSubQuestionEditor();

  return null;
};

export default QuestionRenderer;
